// Settings Panel Component with Theme Selection and AI Features
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useTheme, THEMES } from '../../contexts/ThemeContext.jsx';
import { useAI, AI_FEATURES, DEFAULT_PROMPTS } from '../../contexts/AIContext.jsx';
import { deleteAllNotes } from '../../services/firestore.js';
import Icon from '../common/Icon';
import {
    loadBYOKConfig,
    saveBYOKConfig,
    getBYOKEnabled,
    setBYOKEnabled,
    getActiveProvider,
    setActiveProvider,
    getActiveModel,
    setActiveModel,
    getProviderCredentials,
    updateProviderCredentials,
    testProviderConnection,
    PROVIDER_INFO,
    DEFAULT_MODELS
} from '../../services/byok-config.js';
import './Settings.css';

export default function Settings() {
    const { user } = useAuth();
    const { allNotes } = useNotes();
    const { theme, setTheme } = useTheme();
    const {
        aiEnabled, aiConfigured, toggleAI, aiError,
        customPrompts, updatePrompt, resetPrompts,
        credits, creditsLoading, refreshCredits, getCreditsDisplay
    } = useAI();
    const [deleting, setDeleting] = useState(false);
    const [showPromptEditor, setShowPromptEditor] = useState(null); // 'improve' | 'expand' | 'continue' | null

    // BYOK State
    const [byokEnabled, setByokEnabledState] = useState(false);
    const [byokProvider, setByokProviderState] = useState('openai');
    const [byokApiKey, setByokApiKey] = useState('');
    const [byokEndpoint, setByokEndpoint] = useState('http://localhost:11434');
    const [byokModel, setByokModel] = useState('');
    const [byokModels, setByokModels] = useState([]);
    const [byokExpanded, setByokExpanded] = useState(false);
    const [byokTesting, setByokTesting] = useState(false);
    const [byokSaving, setByokSaving] = useState(false);
    const [byokStatus, setByokStatus] = useState({ message: '', type: '' });

    // Load BYOK config on mount
    useEffect(() => {
        loadBYOKConfig();
        const enabled = getBYOKEnabled();
        const provider = getActiveProvider();
        const model = getActiveModel();
        const credentials = getProviderCredentials(provider);

        setByokEnabledState(enabled);
        setByokProviderState(provider);
        setByokModel(model);

        if (credentials) {
            if (PROVIDER_INFO[provider]?.requiresKey) {
                setByokApiKey(credentials.apiKey || '');
            } else {
                setByokEndpoint(credentials.endpoint || 'http://localhost:11434');
            }
            setByokModels(credentials.models || DEFAULT_MODELS[provider] || []);
        }
    }, []);

    // Fetch credits when component mounts and user is logged in
    useEffect(() => {
        if (user && aiEnabled) {
            refreshCredits();
        }
    }, [user, aiEnabled, refreshCredits]);

    const handleExport = () => {
        const exportData = {
            exportDate: new Date().toISOString(),
            noteCount: allNotes.length,
            notes: allNotes.map(note => ({
                key: note.key,
                domain: note.domain,
                content: note.content,
                updatedAt: note.updatedAt?.toDate?.()?.toISOString() || null,
                createdAt: note.createdAt?.toDate?.()?.toISOString() || null
            }))
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `page-notes-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // BYOK handlers
    const handleByokToggle = useCallback(() => {
        const newEnabled = !byokEnabled;
        setByokEnabledState(newEnabled);
        setBYOKEnabled(newEnabled);

        if (newEnabled) {
            setByokExpanded(true);
            setByokStatus({ message: 'Configure your API settings below', type: 'info' });
        } else {
            setByokStatus({ message: 'Using Page Notes AI (Credits)', type: 'info' });
        }
    }, [byokEnabled]);

    const handleProviderChange = useCallback((e) => {
        const provider = e.target.value;
        setByokProviderState(provider);
        setActiveProvider(provider);

        // Load credentials for the new provider
        const credentials = getProviderCredentials(provider);
        if (credentials) {
            if (PROVIDER_INFO[provider]?.requiresKey) {
                setByokApiKey(credentials.apiKey || '');
            } else {
                setByokEndpoint(credentials.endpoint || 'http://localhost:11434');
            }
            setByokModels(credentials.models || DEFAULT_MODELS[provider] || []);
            setByokModel(credentials.selectedModel || '');
        } else {
            setByokApiKey('');
            setByokEndpoint('http://localhost:11434');
            setByokModels(DEFAULT_MODELS[provider] || []);
            setByokModel('');
        }

        setByokStatus({ message: `Switched to ${PROVIDER_INFO[provider]?.name || provider}`, type: 'info' });
    }, []);

    const handleModelChange = useCallback((e) => {
        const model = e.target.value;
        setByokModel(model);
        setActiveModel(model);
    }, []);

    const handleTestConnection = useCallback(async () => {
        const providerInfo = PROVIDER_INFO[byokProvider];

        // Build credentials object
        const credentials = {};
        if (providerInfo?.requiresKey) {
            credentials.apiKey = byokApiKey;
            if (!credentials.apiKey) {
                setByokStatus({ message: 'Please enter an API key first', type: 'error' });
                return;
            }
        } else {
            credentials.endpoint = byokEndpoint;
        }

        setByokTesting(true);
        setByokStatus({ message: 'Testing connection...', type: 'info' });

        try {
            const result = await testProviderConnection(byokProvider, credentials);

            if (result.success) {
                setByokStatus({ message: result.message, type: 'success' });

                // Update models if returned
                if (result.models && result.models.length > 0) {
                    setByokModels(result.models);
                    updateProviderCredentials(byokProvider, {
                        ...credentials,
                        models: result.models
                    });
                }
            } else {
                setByokStatus({ message: result.message, type: 'error' });
            }
        } catch (error) {
            setByokStatus({ message: error.message || 'Connection test failed', type: 'error' });
        } finally {
            setByokTesting(false);
        }
    }, [byokProvider, byokApiKey, byokEndpoint]);

    const handleSaveByokSettings = useCallback(async () => {
        const providerInfo = PROVIDER_INFO[byokProvider];

        // Build credentials object
        const credentials = {};
        if (providerInfo?.requiresKey) {
            credentials.apiKey = byokApiKey;
        } else {
            credentials.endpoint = byokEndpoint;
        }

        if (byokModel) {
            credentials.selectedModel = byokModel;
        }

        setByokSaving(true);

        try {
            updateProviderCredentials(byokProvider, credentials);

            if (byokModel) {
                setActiveModel(byokModel);
            }

            setByokStatus({ message: 'Settings saved successfully!', type: 'success' });

            // Auto-collapse after successful save
            setTimeout(() => {
                setByokExpanded(false);
                setByokStatus({ message: '', type: '' });
            }, 2000);
        } catch (error) {
            setByokStatus({ message: 'Failed to save settings', type: 'error' });
        } finally {
            setByokSaving(false);
        }
    }, [byokProvider, byokApiKey, byokEndpoint, byokModel]);

    const handleDeleteAll = async () => {
        if (!user?.uid) {
            alert('You must be logged in to delete notes');
            return;
        }

        if (allNotes.length === 0) {
            alert('No notes to delete');
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete all ${allNotes.length} notes? This action cannot be undone.`
        );

        if (!confirmed) return;

        const doubleConfirmed = window.confirm(
            'This will PERMANENTLY delete all your notes from the cloud. Are you absolutely sure?'
        );

        if (!doubleConfirmed) return;

        setDeleting(true);
        try {
            const deletedCount = await deleteAllNotes(user.uid);
            alert(`Successfully deleted ${deletedCount} notes.`);
        } catch (error) {
            console.error('Error deleting notes:', error);
            alert(`Failed to delete notes: ${error.message}`);
        } finally {
            setDeleting(false);
        }
    };

    const promptLabels = {
        improve: { name: 'Improve Writing', icon: 'Sparkles', desc: 'Restructure and clean up content' },
        expand: { name: 'Expand Ideas', icon: 'FileText', desc: 'Add details and elaboration' },
        continue: { name: 'Continue Writing', icon: 'PenTool', desc: 'Continue from last paragraph' }
    };

    return (
        <div className="settings-panel">
            <h2 className="panel-title">Settings</h2>

            <div className="settings-card">
                {/* AI Features Section */}
                <div className="settings-section ai-section">
                    <div className="settings-title"><Icon name="Sparkles" className="mb-1" /> AI Features</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">Enable AI</div>
                            <div className="setting-desc">
                                {aiConfigured
                                    ? 'Powered by Gemini AI'
                                    : 'Add VITE_GEMINI_API_KEY to .env.local'}
                            </div>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={aiEnabled}
                                onChange={toggleAI}
                                disabled={!aiConfigured}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {aiError && (
                        <div className="ai-error">{aiError}</div>
                    )}

                    {aiEnabled && aiConfigured && (
                        <>
                            {/* AI Credits Display */}
                            {user && (
                                <div className="ai-credits-section">
                                    <div className="ai-credits-header">
                                        <span className="ai-credits-icon"><Icon name="Zap" /></span>
                                        <span className="ai-credits-label">AI Credits</span>
                                    </div>
                                    <div className="ai-credits-display">
                                        {creditsLoading ? (
                                            <span className="ai-credits-loading">Loading...</span>
                                        ) : credits?.isPro ? (
                                            <span className="ai-credits-value">{getCreditsDisplay()}</span>
                                        ) : (
                                            <span className="ai-credits-upgrade">Live required for AI features</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="ai-features-list">
                                <div className="ai-features-header">Active features:</div>
                                {AI_FEATURES.map(feature => (
                                    <div key={feature.id} className="ai-feature-item">
                                        <span className="ai-feature-icon">
                                            {/* Map feature icons if necessary, otherwise use generic */}
                                            <Icon name="Star" size="sm" />
                                        </span>
                                        <div className="ai-feature-info">
                                            <span className="ai-feature-name">{feature.name}</span>
                                            <span className="ai-feature-desc">{feature.description}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Custom Prompts Section */}
                            <div className="ai-prompts-section">
                                <div className="ai-prompts-header">
                                    <span>Custom AI Prompts</span>
                                    <button
                                        className="reset-prompts-btn"
                                        onClick={resetPrompts}
                                        title="Reset all prompts to defaults"
                                    >
                                        Reset
                                    </button>
                                </div>
                                <div className="ai-prompts-list">
                                    {Object.entries(promptLabels).map(([key, label]) => (
                                        <div key={key} className="ai-prompt-item">
                                            <div
                                                className="ai-prompt-header"
                                                onClick={() => setShowPromptEditor(showPromptEditor === key ? null : key)}
                                            >
                                                <span className="ai-prompt-icon"><Icon name={label.icon} size="sm" /></span>
                                                <div className="ai-prompt-info">
                                                    <span className="ai-prompt-name">{label.name}</span>
                                                    <span className="ai-prompt-desc">{label.desc}</span>
                                                </div>
                                                <span className={`ai-prompt-toggle ${showPromptEditor === key ? 'open' : ''}`}>
                                                    <Icon name={showPromptEditor === key ? "ChevronDown" : "ChevronRight"} size="sm" />
                                                </span>
                                            </div>
                                            {showPromptEditor === key && (
                                                <div className="ai-prompt-editor">
                                                    <textarea
                                                        value={customPrompts[key]}
                                                        onChange={(e) => updatePrompt(key, e.target.value)}
                                                        placeholder={DEFAULT_PROMPTS[key]}
                                                        rows={6}
                                                    />
                                                    <div className="ai-prompt-hint">
                                                        Edit the system prompt for this feature. Changes are saved automatically.
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {!aiConfigured && (
                        <div className="ai-setup-hint">
                            <span className="ai-hint-icon"><Icon name="Lightbulb" /></span>
                            <span>Get your free API key from <a href="https://aistudio.google.com/" target="_blank" rel="noopener noreferrer">Google AI Studio</a></span>
                        </div>
                    )}
                </div>

                {/* AI Provider (BYOK) Section */}
                <div className="settings-section byok-section">
                    <div className="settings-title"><Icon name="Box" className="mb-1" /> AI Provider</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">AI Service</div>
                            <div className="setting-desc">
                                Choose between Page Notes AI or your own API keys
                            </div>
                        </div>
                        <div className="ai-mode-selector">
                            <select
                                value={byokEnabled ? 'byok' : 'pagenotes'}
                                onChange={(e) => {
                                    const newEnabled = e.target.value === 'byok';
                                    setByokEnabledState(newEnabled);
                                    setBYOKEnabled(newEnabled);
                                    if (newEnabled) {
                                        setByokExpanded(true);
                                    }
                                }}
                                className="ai-mode-select"
                            >
                                <option value="pagenotes">Page Notes AI (Credits)</option>
                                <option value="byok">Use Your Own API Key</option>
                            </select>
                        </div>
                    </div>

                    {byokEnabled && (
                        <div className={`byok-settings ${byokExpanded ? 'expanded' : 'collapsed'}`}>
                            <button
                                className="byok-expand-toggle"
                                onClick={() => setByokExpanded(!byokExpanded)}
                            >
                                <span>
                                    <Icon name={byokExpanded ? "ChevronDown" : "ChevronRight"} size="sm" className="inline mr-1" />
                                    Configure API Settings
                                </span>
                                {!byokExpanded && byokProvider && (
                                    <span className="byok-provider-badge">
                                        {PROVIDER_INFO[byokProvider]?.name || byokProvider}
                                    </span>
                                )}
                            </button>

                            {byokExpanded && (
                                <div className="byok-config-panel">
                                    {/* Provider Selection */}
                                    <div className="byok-form-group">
                                        <label>Provider</label>
                                        <select
                                            value={byokProvider}
                                            onChange={handleProviderChange}
                                            className="byok-select"
                                        >
                                            <option value="openai">OpenAI</option>
                                            <option value="anthropic">Anthropic</option>
                                            <option value="openrouter">OpenRouter</option>
                                            <option value="huggingface">HuggingFace</option>
                                            <option value="ollama">Ollama (Local)</option>
                                        </select>
                                    </div>

                                    {/* API Key (hidden for Ollama) */}
                                    {PROVIDER_INFO[byokProvider]?.requiresKey && (
                                        <div className="byok-form-group">
                                            <label>API Key</label>
                                            <input
                                                type="password"
                                                value={byokApiKey}
                                                onChange={(e) => setByokApiKey(e.target.value)}
                                                placeholder={PROVIDER_INFO[byokProvider]?.keyPlaceholder || 'Enter API key...'}
                                                className="byok-input"
                                            />
                                            <span className="byok-hint">
                                                Get your key from{' '}
                                                <a
                                                    href={PROVIDER_INFO[byokProvider]?.keyUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {PROVIDER_INFO[byokProvider]?.name} website
                                                </a>
                                            </span>
                                        </div>
                                    )}

                                    {/* Endpoint (for Ollama) */}
                                    {!PROVIDER_INFO[byokProvider]?.requiresKey && (
                                        <div className="byok-form-group">
                                            <label>Ollama URL</label>
                                            <input
                                                type="text"
                                                value={byokEndpoint}
                                                onChange={(e) => setByokEndpoint(e.target.value)}
                                                placeholder="http://localhost:11434"
                                                className="byok-input"
                                            />
                                            <span className="byok-hint">
                                                Make sure Ollama is running locally
                                            </span>
                                        </div>
                                    )}

                                    {/* Model Selection */}
                                    <div className="byok-form-group">
                                        <label>Model</label>
                                        <select
                                            value={byokModel}
                                            onChange={handleModelChange}
                                            className="byok-select"
                                        >
                                            <option value="" disabled>Select a model...</option>
                                            {byokModels.map(model => (
                                                <option key={model} value={model}>{model}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Actions */}
                                    <div className="byok-actions">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleTestConnection}
                                            disabled={byokTesting}
                                        >
                                            {byokTesting ? 'Testing...' : 'Test Connection'}
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSaveByokSettings}
                                            disabled={byokSaving}
                                        >
                                            {byokSaving ? 'Saving...' : 'Save Settings'}
                                        </button>
                                    </div>

                                    {/* Status */}
                                    {byokStatus.message && (
                                        <div className={`byok-status byok-status--${byokStatus.type}`}>
                                            {byokStatus.message}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Appearance Section */}
                <div className="settings-section">
                    <div className="settings-title">Appearance</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">Theme</div>
                            <div className="setting-desc">Choose your preferred color scheme</div>
                        </div>
                        <div className="theme-selector">
                            <button
                                className={`theme-btn ${theme === THEMES.DARK ? 'active' : ''}`}
                                onClick={() => setTheme(THEMES.DARK)}
                                title="Dark theme"
                            >
                                <Icon name="Moon" />
                            </button>
                            <button
                                className={`theme-btn ${theme === THEMES.LIGHT ? 'active' : ''}`}
                                onClick={() => setTheme(THEMES.LIGHT)}
                                title="Light theme"
                            >
                                <Icon name="Sun" />
                            </button>
                            <button
                                className={`theme-btn ${theme === THEMES.SYSTEM ? 'active' : ''}`}
                                onClick={() => setTheme(THEMES.SYSTEM)}
                                title="System theme"
                            >
                                <Icon name="Monitor" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Data Section */}
                <div className="settings-section">
                    <div className="settings-title">Data</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">Export Notes</div>
                            <div className="setting-desc">Download all {allNotes.length} notes as JSON</div>
                        </div>
                        <button className="btn btn-secondary" onClick={handleExport}>
                            Export
                        </button>
                    </div>
                </div>

                {/* Storage Section */}
                <div className="settings-section">
                    <div className="settings-title">Storage</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">Total Notes</div>
                            <div className="setting-desc">{allNotes.length} notes stored in the cloud</div>
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="settings-section danger-zone">
                    <div className="settings-title danger"><Icon name="AlertTriangle" className="mb-1" /> Danger Zone</div>
                    <div className="setting-item">
                        <div>
                            <div className="setting-label">Delete All Notes</div>
                            <div className="setting-desc">
                                Permanently delete all your notes. This action cannot be undone.
                            </div>
                        </div>
                        <button
                            className="btn btn-danger"
                            onClick={handleDeleteAll}
                            disabled={deleting || allNotes.length === 0}
                        >
                            {deleting ? 'Deleting...' : 'Delete All'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
